import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';

import { TopicField } from '@app/origami-api/utils/topic-utils';
import { ErrorCode } from '@app/origami-api/constants/error-codes';
import { ServiceBase, OrigamiResponse } from './base/service.base';
import { ExportDataFormat } from '../constants/export-data-format';

export enum SearchConfigUsage {
  Search = 1,
  Indexing = 2,
  SearchAndIndexing = 3,
}

export interface ISearchDocTopicTypes {
  Id: number;
  Name: string;
  RelatedTopicTypeId: number;
  RelatedTopicTypeName: string;
  ApplySecurity: boolean;
  DomainTopicTypeId: number;
  IndexingQueueActionBitValue: number;
}

export interface IGetIndexConfig {
  Enabled: boolean;
  EntityType: string;
  Id: number;
  Scope: number;
  TopicTypes: ISearchDocTopicTypes[];
  Usage: SearchConfigUsage.Search | SearchConfigUsage.Indexing | SearchConfigUsage.SearchAndIndexing;
}

export interface ISetSearchConfig {
  Enabled: boolean;
  Id: number;
}

export interface IAggregationBucket {
  EntitiesCount: number;
  FieldName: string;
  Key: string;
  isChecked?: boolean;
}

export interface ISearch {
  roomId: number;
  searchInRoomIds: number[];
  searchEntityType: string; //todo
  searchBy: {
    MainQuery: string;
    FtsQuery: {
      Query: string;
      ByFields: boolean;
      ByFileContent: boolean;
    };
  };
  aggregateBy?: {
    FieldName: string;
    MaxBucketCount: number;
    IntroduceNotSetBucketForFieldsOfTypes: number[];
  };
  pager?: {
    Start?: number;
    PageSize: number;
  };
  fields?: string[];
  exportParam?: {
    ExportFormat: ExportDataFormat;
  };
}

export interface ISearchResultsEntities {
  Fields: TopicField[];
  Id: number;
}

export interface ISearchResults {
  AggregationBuckets: IAggregationBucket[];
  Entities: ISearchResultsEntities[];
  Total: number;
}

export interface IShareSearch {
  roomId: number;
  topicIds: number[];
  userNames: string[];
  groupNames: string[];
  minRoleLevel: number;
}

export interface IExportSearch {
  JobId: string;
  Total: number;
}

export interface ISaveSearchParams {
  siteId: number;
  userId: number;
  search: string;
}

export interface IRecentSearchParams {
  siteId: number;
  userId: number;
  limit: number;
}

export interface RecentSearchResponse {
  CreatedDate: Date;
  RecentSeachId: number;
  Search: string;
  UserId: number;
}

@Injectable()
export class IndexSearchService extends ServiceBase {
  serviceUrl = 'api/indexsearch';
  totalRecords: number;
  searchParams: ISearch;
  selectedDocTopicsIDs: number[] = [];

  getIndexConfig(roomId: number): Observable<OrigamiResponse<IGetIndexConfig[]>> {
    const url = `/indexconfig?roomId=${roomId}`;
    return this.origamiGet(url);
  }

  search(
    body: ISearch,
    force?: boolean,
    silentErrorCodes?: ErrorCode[],
  ): Observable<OrigamiResponse<ISearchResults & IExportSearch>> {
    silentErrorCodes = silentErrorCodes || null;
    /*
    return of(<any>{
      ErrorCode: 0,
      Result: {
        AggregationBuckets: [
          {
            Documents: [],
            EntitiesCount: 1004,
            FieldName: '$$DocumentTypeName$$',
            Key: 'Doc Type 1',
          },
          {
            Documents: [],
            EntitiesCount: 204,
            FieldName: '$$DocumentTypeName$$',
            Key: 'Doc Type 2',
          },
          {
            Documents: [],
            EntitiesCount: 43,
            FieldName: '$$DocumentTypeName$$',
            Key: 'not cc workflow doc type',
          },
          {
            Documents: [],
            EntitiesCount: 12,
            FieldName: '$$DocumentTypeName$$',
            Key: 'Doc Type 3',
          },
          {
            Documents: [],
            EntitiesCount: 10,
            FieldName: '$$DocumentTypeName$$',
            Key: 'Doc Type 4',
          },
          {
            Documents: [],
            EntitiesCount: 6,
            FieldName: '$$DocumentTypeName$$',
            Key: 'Doc Type 5',
          },
          {
            Documents: [],
            EntitiesCount: 3,
            FieldName: '$$DocumentTypeName$$',
            Key: 'DK DocType 01',
          },
          {
            Documents: [],
            EntitiesCount: 2,
            FieldName: '$$DocumentTypeName$$',
            Key: 'Doc Type',
          },
          {
            Documents: [],
            EntitiesCount: 2,
            FieldName: '$$DocumentTypeName$$',
            Key: 'Doc Type 7',
          },
          {
            Documents: [],
            EntitiesCount: 2,
            FieldName: '$$DocumentTypeName$$',
            Key: 'Doc Type 8  Hubert Blaine Wolfeschlegelsteinhausenbergerdorff Sr Hubert Blaine Wolfeschlegelsteinhausenbergerdorff SrHubert Blaine Wolfeschlegelsteinhausenbergerdorff Sr',
          },
        ],
        Entities: [],
        Total: 1292,
      },
    });
    */

    return this.origamiPost('/search', body, null, silentErrorCodes, force);
  }

  setSearchConfig(body: ISetSearchConfig, ...params): Observable<OrigamiResponse<any>> {
    return this.origamiPost('/searchConfig', body, ...params);
  }

  shareSavedSearch(body: IShareSearch, silentErrorCodes?: ErrorCode[]): Observable<OrigamiResponse<unknown>> {
    return this.origamiPost('/shareFilter', body, null, silentErrorCodes, true);
  }

  getRecentSearches(
    body: IRecentSearchParams,
    silentErrorCodes?: ErrorCode[],
  ): Observable<OrigamiResponse<RecentSearchResponse[]>> {
    return this.origamiPost('/GetRecentSearches', body, null, silentErrorCodes, true);
  }

  saveSearch(body: ISaveSearchParams, silentErrorCodes?: ErrorCode[]): Observable<OrigamiResponse<unknown>> {
    return this.origamiPost('/SaveSearch', body, null, silentErrorCodes, true);
  }
}
